import { fetchGoalsByARI, type Node } from '../../../../services/goals';
import type { LazyGoal, StoreApi, ContainerProps } from '../types';
import { transformToGoal } from '../utils';

const asInitialLazyGoal = (goalARIs: string[]): Record<string, LazyGoal> =>
	goalARIs.reduce<Record<string, LazyGoal>>((acc, goalARI) => {
		acc[goalARI] = { isLoading: true, goal: undefined };
		return acc;
	}, {});

const fetchAndSetLazyGoalsByARI = (
	newGoalARIs: string[],
	setState: StoreApi['setState'],
	prevLazyGoalsByARIs: Record<string, LazyGoal> = {},
) => {
	fetchGoalsByARI(newGoalARIs).then((goalPromises: PromiseSettledResult<Node[]>[]) => {
		let isError = false;
		const goalNodes: Node[] = [];

		goalPromises.forEach((promiseResult) => {
			if (promiseResult.status === 'fulfilled') {
				goalNodes.push(...promiseResult.value);
			} else if (promiseResult.reason instanceof Error) {
				if (promiseResult.reason.message !== 'Permission') {
					isError = true;
				}
			}
		});

		const goals = goalNodes.map(transformToGoal);
		const lazyGoalsByARI = goals.reduce<Record<string, LazyGoal>>((acc, goal) => {
			acc[goal.id] = { isLoading: false, goal };
			return acc;
		}, prevLazyGoalsByARIs);

		// reset loading state of unknown goals, e.g. goals that were deleted
		newGoalARIs.reduce<Record<string, LazyGoal>>((acc, goalARI) => {
			if (!(goalARI in acc)) {
				acc[goalARI] = { isLoading: false, goal: undefined };
			}
			return acc;
		}, lazyGoalsByARI);

		setState({
			lazyGoalsByARI,
			...(isError ? { isError } : {}),
		});
	});
};

export const handlers = {
	onInit:
		() =>
		({ setState }: StoreApi, { goalARIs, isAtlasConnectInstalled }: ContainerProps) => {
			if (isAtlasConnectInstalled && goalARIs.length > 0) {
				// set initial loading state
				setState({ lazyGoalsByARI: asInitialLazyGoal(goalARIs) });
				fetchAndSetLazyGoalsByARI(goalARIs, setState);
			}
		},
	onContainerUpdate:
		() =>
		({ getState, setState }: StoreApi, { goalARIs, isAtlasConnectInstalled }: ContainerProps) => {
			if (isAtlasConnectInstalled && goalARIs.length > 0) {
				const { lazyGoalsByARI: prevLazyGoalsByARIs } = getState();
				const difference = goalARIs.reduce<string[]>((acc, goalARI) => {
					if (prevLazyGoalsByARIs[goalARI] === undefined) {
						acc.push(goalARI);
					}
					return acc;
				}, []);
				if (difference.length > 0) {
					// set initial loading state
					setState({
						lazyGoalsByARI: {
							...prevLazyGoalsByARIs,
							...asInitialLazyGoal(difference),
						},
					});
					fetchAndSetLazyGoalsByARI(difference, setState, prevLazyGoalsByARIs);
				}
			}
		},
};
